import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { kycAPI } from '../../utils/api';
import {
  formatDate,
  getStatusBadgeClass,
  formatStatusText,
} from '../../utils/helpers';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { cn } from '../../lib/utils';
import {
  ArrowRight,
  Check,
  AlertCircle,
  CheckCircle,
  Upload,
  ChevronDown,
  Calendar,
} from 'lucide-react';
import { countries } from '../../utils/constants';

// Step Indicator Component
function StepIndicator({ currentStep }) {
  const steps = [
    { id: 1, label: 'Personal Info' },
    { id: 2, label: 'ID Verification' },
    { id: 3, label: 'Address Proof' },
    { id: 4, label: 'Financial Info' },
    { id: 5, label: 'Compliance' },
  ];

  return (
    <div className="relative mb-12 px-4">
      {/* Progress Line (desktop) */}
      <div className="absolute top-3 left-0 w-full h-0.5 bg-gray-200 -z-10 hidden md:block" />
      <div
        className="absolute top-3 left-0 h-0.5 bg-[#062136] -z-10 transition-all duration-500 hidden md:block"
        style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
      />

      <div className="flex w-full overflow-x-auto md:overflow-visible">
        <div className="flex w-max md:w-full md:justify-between gap-6 md:gap-0 pb-2 md:pb-0">
          {steps.map((step) => (
            <div
              key={step.id}
              className="flex min-w-[110px] flex-col items-center gap-2"
            >
              <div
                className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-[3px] transition-colors duration-300',
                  currentStep > step.id
                    ? 'bg-[#062136] border-[#062136] text-white'
                    : currentStep === step.id
                      ? 'border-[#062136] text-[#062136] bg-white'
                      : 'border-gray-300 text-gray-400 bg-white'
                )}
              >
                {currentStep > step.id ? (
                  <Check size={12} />
                ) : currentStep === step.id ? (
                  <div className="w-2 h-2 bg-[#062136] rounded-full" />
                ) : null}
              </div>
              <span
                className={cn(
                  'text-xs font-medium transition-colors text-center',
                  currentStep >= step.id ? 'text-gray-900' : 'text-gray-400'
                )}
              >
                {step.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Step 1: Personal Information
function PersonalInfoStep({ formData, setFormData, onNext }) {
  const [errors, setErrors] = useState({});
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
      setFormData((prevData) => ({
        ...prevData,
        email: JSON.parse(storedUser).email,
      }));
    }
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!formData.first_name) newErrors.first_name = 'First name is required';
    if (!formData.last_name) newErrors.last_name = 'Last name is required';
    if (!formData.date_of_birth) newErrors.date_of_birth = 'Date of birth is required';
    if (!formData.nationality) newErrors.nationality = 'Nationality is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    if (!formData.gender) newErrors.gender = 'Gender is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-900">
          📋 Please provide accurate personal information. This data will be used for verification.
        </p>
      </div>

      {/* Name Fields */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className="text-gray-900">
            First Name <span className="text-red-500">*</span>
          </Label>
          <Input
            placeholder="Enter your first name"
            value={formData.first_name}
            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900 placeholder:text-gray-400"
          />
          {errors.first_name && (
            <p className="text-red-500 text-xs">{errors.first_name}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label className="text-gray-900">
            Last Name <span className="text-red-500">*</span>
          </Label>
          <Input
            placeholder="Enter your last name"
            value={formData.last_name}
            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900 placeholder:text-gray-400"
          />
          {errors.last_name && (
            <p className="text-red-500 text-xs">{errors.last_name}</p>
          )}
        </div>
      </div>

      {/* Date of Birth */}
      <div className="space-y-2">
        <Label className="text-gray-900">
          Date of Birth <span className="text-red-500">*</span>
        </Label>
        <Input
          type="date"
          value={formData.date_of_birth}
          min={new Date(new Date().setFullYear(new Date().getFullYear() - 100)).toISOString().split('T')[0]}
          max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
          onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
          className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900"
        />
        {errors.date_of_birth && (
          <p className="text-red-500 text-xs">{errors.date_of_birth}</p>
        )}
      </div>

      {/* Gender */}
      <div className="space-y-2">
        <Label className="text-gray-900">
          Gender <span className="text-red-500">*</span>
        </Label>
        <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
          <SelectTrigger className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900">
            <SelectValue placeholder="Select gender" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="male">Male</SelectItem>
            <SelectItem value="female">Female</SelectItem>
            <SelectItem value="other">Other</SelectItem>
            <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
          </SelectContent>
        </Select>
        {errors.gender && <p className="text-red-500 text-xs">{errors.gender}</p>}
      </div>

      {/* Nationality */}
      <div className="space-y-2">
        <Label className="text-gray-900">
          Nationality <span className="text-red-500">*</span>
        </Label>
        <Select
          value={formData.nationality}
          onValueChange={(value) => setFormData({ ...formData, nationality: value })}
        >
          <SelectTrigger className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900">
            <SelectValue placeholder="Please select" />
          </SelectTrigger>
          <SelectContent className="max-h-60">
            {countries.map((country) => (
              <SelectItem key={country.iso2} value={country.iso2}>
                {country.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.nationality && (
          <p className="text-red-500 text-xs">{errors.nationality}</p>
        )}
      </div>

      {/* Contact Information */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className="text-gray-900">
            Email <span className="text-red-500">*</span>
          </Label>
          <Input
            type="email"
            // placeholder="Enter your email address"
            value={user?.email || formData.email}
            onChange={() => {
              if (user && user?.email) {
                setFormData({ ...formData, email: user.email });
              }
            }}
            className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900 placeholder:text-gray-400"
            disabled
          />
          {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
        </div>
        <div className="space-y-2">
          <Label className="text-gray-900">
            Phone Number <span className="text-red-500">*</span>
          </Label>
          <Input
            placeholder="+1 (555) 000-0000"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900 placeholder:text-gray-400"
          />
          {errors.phone && <p className="text-red-500 text-xs">{errors.phone}</p>}
        </div>
      </div>

      <div className="pt-4">
        <Button
          onClick={handleNext}
          className="bg-[#062136] hover:bg-[#062136]/90 text-white font-semibold h-12 px-8 rounded-xl shadow-lg w-full"
        >
          <ArrowRight className="mr-2 w-4 h-4" /> Continue to ID Verification
        </Button>
      </div>
    </motion.div>
  );
}

// Step 2: ID Verification
function IDVerificationStep({ files, setFiles, formData, setFormData, onNext, onBack }) {
  const [errors, setErrors] = useState({});
  const idTypeConfig = {
    passport: {
      label: 'Passport Number',
      placeholder: 'Enter your passport number',
      uploads: [
        { key: 'id_front', label: 'Passport Photo Page', required: true },
        { key: 'selfie', label: 'Selfie Photo', required: true, selfie: true },
      ],
    },
    national_id: {
      label: 'National ID Number',
      placeholder: 'Enter your national ID number',
      uploads: [
        { key: 'id_front', label: 'ID Front Side', required: true },
        { key: 'id_back', label: 'ID Back Side', required: true },
        { key: 'selfie', label: 'Selfie Photo', required: true, selfie: true },
      ],
    },
    driver_license: {
      label: "Driver's License Number",
      placeholder: "Enter your driver's license number",
      uploads: [
        { key: 'id_front', label: "Driver's License Front Side", required: true },
        { key: 'id_back', label: "Driver's License Back Side", required: true },
        { key: 'selfie', label: 'Selfie Photo', required: true, selfie: true },
      ],
    },
    residence_permit: {
      label: 'Residence Permit Number',
      placeholder: 'Enter your residence permit number',
      uploads: [
        { key: 'id_front', label: 'Residence Permit Front Side', required: true },
        { key: 'id_back', label: 'Residence Permit Back Side', required: true },
        { key: 'selfie', label: 'Selfie Photo', required: true, selfie: true },
      ],
    },
  };
  const idNumberLabel = idTypeConfig[formData.id_type]?.label || 'ID Number';
  const idNumberPlaceholder = idTypeConfig[formData.id_type]?.placeholder || 'Enter your ID number';
  const uploadFields = idTypeConfig[formData.id_type]?.uploads || [];

  const validate = () => {
    const newErrors = {};
    if (!formData.id_type) newErrors.id_type = 'ID type is required';
    if (!formData.id_number) newErrors.id_number = 'ID number is required';
    if (!formData.id_expiry) newErrors.id_expiry = 'ID expiry date is required';
    // Validate only required upload fields for selected ID type
    uploadFields.forEach(field => {
      if (field.required && !files[field.key]) {
        newErrors[field.key] = `${field.label} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-900">
          🪪 Upload clear photos of your government-issued ID. Ensure all information is visible.
        </p>
      </div>

      {/* ID Type */}
      <div className="space-y-2">
        <Label className="text-gray-900">
          ID Type <span className="text-red-500">*</span>
        </Label>
        <Select
          value={formData.id_type}
          onValueChange={(value) => setFormData({ ...formData, id_type: value })}
        >
          <SelectTrigger className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900">
            <SelectValue placeholder="Select ID type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="passport">Passport</SelectItem>
            <SelectItem value="national_id">National ID Card</SelectItem>
            <SelectItem value="driver_license">Driver's License</SelectItem>
            <SelectItem value="residence_permit">Residence Permit</SelectItem>
          </SelectContent>
        </Select>
        {errors.id_type && <p className="text-red-500 text-xs">{errors.id_type}</p>}
      </div>

      {/* ID Number */}
      <div className="space-y-2">
        <Label className="text-gray-900">
          {idNumberLabel} <span className="text-red-500">*</span>
        </Label>
        <Input
          placeholder={idNumberPlaceholder}
          value={formData.id_number}
          onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
          className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900 placeholder:text-gray-400"
        />
        {errors.id_number && <p className="text-red-500 text-xs">{errors.id_number}</p>}
      </div>

      {/* ID Expiry Date */}
      <div className="space-y-2">
        <Label className="text-gray-900">
          ID Expiry Date <span className="text-red-500">*</span>
        </Label>
        <Input
          type="date"
          value={formData.id_expiry}
          min={new Date().toISOString().split('T')[0]}
          onChange={(e) => setFormData({ ...formData, id_expiry: e.target.value })}
          className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900"
        />
        {errors.id_expiry && <p className="text-red-500 text-xs">{errors.id_expiry}</p>}
      </div>

      {/* Dynamic ID Uploads */}
      <div className={`grid gap-6 md:grid-cols-${uploadFields.length}`}>
        {uploadFields.map((field) => (
          <FileUpload
            key={field.key}
            label={field.label}
            value={files[field.key]}
            onChange={(file) => setFiles({ ...files, [field.key]: file })}
            accept="image/*,.pdf"
            error={errors[field.key]}
            selfie={field.selfie}
          />
        ))}
      </div>

      <div className="flex items-center justify-between pt-4 gap-4">
        <Button
          variant="outline"
          onClick={onBack}
          className="bg-gray-100 hover:bg-gray-200 text-gray-900 h-12 px-6 rounded-xl flex-1"
        >
          Go Back
        </Button>
        <Button
          onClick={handleNext}
          className="bg-[#062136] hover:bg-[#062136]/90 text-white font-semibold h-12 px-8 rounded-xl shadow-lg flex-1"
        >
          <ArrowRight className="mr-2 w-4 h-4" /> Continue to Address
        </Button>
      </div>
    </motion.div>
  );
}

// Step 3: Address Proof
function AddressProofStep({ files, setFiles, formData, setFormData, onNext, onBack }) {
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.address_line1) newErrors.address_line1 = 'Address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.postal_code) newErrors.postal_code = 'Postal code is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!files.proof_of_address) newErrors.proof_of_address = 'Proof of address is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-900">
          🏠 Provide a recent document (utility bill, bank statement) showing your name and address issued within 3 months.
        </p>
      </div>

      {/* Address Fields */}
      <div className="space-y-2">
        <Label className="text-gray-900">
          Street Address <span className="text-red-500">*</span>
        </Label>
        <Input
          placeholder="Enter your street address"
          value={formData.address_line1}
          onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
          className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900 placeholder:text-gray-400"
        />
        {errors.address_line1 && (
          <p className="text-red-500 text-xs">{errors.address_line1}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-gray-900">Address Line 2 (Optional)</Label>
        <Input
          placeholder="Apartment, suite, etc."
          value={formData.address_line2 || ''}
          onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
          className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900 placeholder:text-gray-400"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className="text-gray-900">
            City <span className="text-red-500">*</span>
          </Label>
          <Input
            placeholder="Enter your city"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900 placeholder:text-gray-400"
          />
          {errors.city && <p className="text-red-500 text-xs">{errors.city}</p>}
        </div>
        <div className="space-y-2">
          <Label className="text-gray-900">
            State/Province (Optional)
          </Label>
          <Input
            placeholder="Enter state or province"
            value={formData.state || ''}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
            className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900 placeholder:text-gray-400"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className="text-gray-900">
            Postal Code <span className="text-red-500">*</span>
          </Label>
          <Input
            placeholder="Enter postal code"
            value={formData.postal_code}
            onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
            className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900 placeholder:text-gray-400"
          />
          {errors.postal_code && (
            <p className="text-red-500 text-xs">{errors.postal_code}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label className="text-gray-900">
            Country <span className="text-red-500">*</span>
          </Label>
          <Select
            value={formData.country}
            onValueChange={(value) => setFormData({ ...formData, country: value })}
          >
            <SelectTrigger className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {[...countries].map((country) => (
                <SelectItem key={country.iso2} value={country.iso2}>
                  {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.country && <p className="text-red-500 text-xs">{errors.country}</p>}
        </div>
      </div>

      {/* Proof of Address Upload */}
      <FileUpload
        label="Proof of Address (Utility Bill, Bank Statement, etc.)"
        value={files.proof_of_address}
        onChange={(file) => setFiles({ ...files, proof_of_address: file })}
        accept="image/*,.pdf"
        error={errors.proof_of_address}
      />

      <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
        <p className="text-xs text-orange-900 flex gap-2">
          <span>⚠️</span>
          <span>Document must be dated within the last 3 months and clearly show your name and address.</span>
        </p>
      </div>

      <div className="flex items-center justify-between pt-4 gap-4">
        <Button
          variant="outline"
          onClick={onBack}
          className="bg-gray-100 hover:bg-gray-200 text-gray-900 h-12 px-6 rounded-xl flex-1"
        >
          Go Back
        </Button>
        <Button
          onClick={handleNext}
          className="bg-[#062136] hover:bg-[#062136]/90 text-white font-semibold h-12 px-8 rounded-xl shadow-lg flex-1"
        >
          <ArrowRight className="mr-2 w-4 h-4" /> Continue to Financial Info
        </Button>
      </div>
    </motion.div>
  );
}

// Step 4: Financial Information
function FinancialInfoStep({ formData, setFormData, onNext, onBack }) {
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.occupation) newErrors.occupation = 'Occupation is required';
    if (!formData.annual_income) newErrors.annual_income = 'Annual income is required';
    if (!formData.source_of_funds) newErrors.source_of_funds = 'Source of funds is required';
    if (!formData.employment_status) newErrors.employment_status = 'Employment status is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-900">
          💼 Provide information about your financial background for compliance purposes.
        </p>
      </div>

      {/* Employment Status */}
      <div className="space-y-2">
        <Label className="text-gray-900">
          Employment Status <span className="text-red-500">*</span>
        </Label>
        <Select
          value={formData.employment_status}
          onValueChange={(value) => setFormData({ ...formData, employment_status: value })}
        >
          <SelectTrigger className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900">
            <SelectValue placeholder="Select employment status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="employed">Employed</SelectItem>
            <SelectItem value="self_employed">Self-Employed</SelectItem>
            <SelectItem value="unemployed">Unemployed</SelectItem>
            <SelectItem value="student">Student</SelectItem>
            <SelectItem value="retired">Retired</SelectItem>
          </SelectContent>
        </Select>
        {errors.employment_status && (
          <p className="text-red-500 text-xs">{errors.employment_status}</p>
        )}
      </div>

      {/* Occupation */}
      <div className="space-y-2">
        <Label className="text-gray-900">
          Occupation <span className="text-red-500">*</span>
        </Label>
        <Input
          placeholder="Enter your occupation"
          value={formData.occupation}
          onChange={(e) => setFormData({ ...formData, occupation: e.target.value })}
          className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900 placeholder:text-gray-400"
        />
        {errors.occupation && <p className="text-red-500 text-xs">{errors.occupation}</p>}
      </div>

      {/* Annual Income */}
      <div className="space-y-2">
        <Label className="text-gray-900">
          Annual Income <span className="text-red-500">*</span>
        </Label>
        <Select
          value={formData.annual_income}
          onValueChange={(value) => setFormData({ ...formData, annual_income: value })}
        >
          <SelectTrigger className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900">
            <SelectValue placeholder="Select annual income range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="less_than_25k">Less than $25,000</SelectItem>
            <SelectItem value="25k_to_50k">$25,000 - $50,000</SelectItem>
            <SelectItem value="50k_to_100k">$50,000 - $100,000</SelectItem>
            <SelectItem value="100k_to_250k">$100,000 - $250,000</SelectItem>
            <SelectItem value="250k_to_500k">$250,000 - $500,000</SelectItem>
            <SelectItem value="500k_to_1m">$500,000 - $1,000,000</SelectItem>
            <SelectItem value="more_than_1m">More than $1,000,000</SelectItem>
          </SelectContent>
        </Select>
        {errors.annual_income && (
          <p className="text-red-500 text-xs">{errors.annual_income}</p>
        )}
      </div>

      {/* Source of Funds */}
      <div className="space-y-2">
        <Label className="text-gray-900">
          Source of Funds <span className="text-red-500">*</span>
        </Label>
        <Select
          value={formData.source_of_funds}
          onValueChange={(value) => setFormData({ ...formData, source_of_funds: value })}
        >
          <SelectTrigger className="bg-gray-50 border-gray-200 shadow-sm h-12 rounded-xl text-gray-900">
            <SelectValue placeholder="Select source of funds" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="employment">Employment Income</SelectItem>
            <SelectItem value="investment">Investment Income</SelectItem>
            <SelectItem value="business">Business Income</SelectItem>
            <SelectItem value="inheritance">Inheritance</SelectItem>
            <SelectItem value="savings">Savings</SelectItem>
            <SelectItem value="gift">Gift</SelectItem>
            <SelectItem value="crypto">Cryptocurrency</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        {errors.source_of_funds && (
          <p className="text-red-500 text-xs">{errors.source_of_funds}</p>
        )}
      </div>

      {/* Additional Information */}
      <div className="space-y-2">
        <Label className="text-gray-900">
          Additional Information (Optional)
        </Label>
        <Textarea
          placeholder="Provide any additional information about your financial background..."
          value={formData.financial_info || ''}
          onChange={(e) => setFormData({ ...formData, financial_info: e.target.value })}
          className="bg-gray-50 border-gray-200 shadow-sm rounded-xl text-gray-900 placeholder:text-gray-400 min-h-[100px]"
        />
      </div>

      <div className="flex items-center justify-between pt-4 gap-4">
        <Button
          variant="outline"
          onClick={onBack}
          className="bg-gray-100 hover:bg-gray-200 text-gray-900 h-12 px-6 rounded-xl flex-1"
        >
          Go Back
        </Button>
        <Button
          onClick={handleNext}
          className="bg-[#062136] hover:bg-[#062136]/90 text-white font-semibold h-12 px-8 rounded-xl shadow-lg flex-1"
        >
          <ArrowRight className="mr-2 w-4 h-4" /> Continue to Compliance
        </Button>
      </div>
    </motion.div>
  );
}

// Step 5: Compliance & Declarations
function ComplianceStep({ formData, setFormData, onSubmit, submitting, onBack }) {
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.politically_exposed_person) newErrors.politically_exposed_person = 'PEP declaration is required';
    if (!formData.sanctions_check) newErrors.sanctions_check = 'Sanctions check is required';
    if (!formData.terms_accepted) newErrors.terms_accepted = 'You must accept the terms';
    if (!formData.privacy_accepted) newErrors.privacy_accepted = 'You must accept the privacy policy';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-900">
          ⚖️ Please declare the following compliance information to complete your KYC.
        </p>
      </div>

      {/* Politically Exposed Person */}
      <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
        <div className="space-y-4">
          <p className="font-medium text-gray-900">
            Politically Exposed Person (PEP) <span className="text-red-500">*</span>
          </p>
          <p className="text-sm text-gray-600">
            Are you or any of your family members a politician, government official, or hold a prominent public position?
          </p>
          <div className="flex items-center space-x-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="pep"
                value="no"
                checked={formData.politically_exposed_person === 'no'}
                onChange={(e) => setFormData({ ...formData, politically_exposed_person: e.target.value })}
                className="w-4 h-4"
              />
              <span className="ml-2 text-gray-700">No</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="pep"
                value="yes"
                checked={formData.politically_exposed_person === 'yes'}
                onChange={(e) => setFormData({ ...formData, politically_exposed_person: e.target.value })}
                className="w-4 h-4"
              />
              <span className="ml-2 text-gray-700">Yes</span>
            </label>
          </div>
          {errors.politically_exposed_person && (
            <p className="text-red-500 text-xs">{errors.politically_exposed_person}</p>
          )}
        </div>
      </div>

      {/* Sanctions Check */}
      <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
        <div className="space-y-4">
          <p className="font-medium text-gray-900">
            Sanctions Screening <span className="text-red-500">*</span>
          </p>
          <p className="text-sm text-gray-600">
            To the best of your knowledge, are you or any of your family members on any sanctions list?
          </p>
          <div className="flex items-center space-x-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="sanctions"
                value="no"
                checked={formData.sanctions_check === 'no'}
                onChange={(e) => setFormData({ ...formData, sanctions_check: e.target.value })}
                className="w-4 h-4"
              />
              <span className="ml-2 text-gray-700">No</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="sanctions"
                value="yes"
                checked={formData.sanctions_check === 'yes'}
                onChange={(e) => setFormData({ ...formData, sanctions_check: e.target.value })}
                className="w-4 h-4"
              />
              <span className="ml-2 text-gray-700">Yes</span>
            </label>
          </div>
          {errors.sanctions_check && (
            <p className="text-red-500 text-xs">{errors.sanctions_check}</p>
          )}
        </div>
      </div>

      {/* Terms Acceptance */}
      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="terms"
            checked={formData.terms_accepted}
            onCheckedChange={(checked) => setFormData({ ...formData, terms_accepted: checked })}
            className="mt-1"
          />
          <label htmlFor="terms" className="text-sm text-gray-700 cursor-pointer">
            I agree to the <span className="text-[#21bcfe] font-semibold">Terms of Service</span> <span className="text-red-500">*</span>
          </label>
        </div>
        {errors.terms_accepted && (
          <p className="text-red-500 text-xs">{errors.terms_accepted}</p>
        )}
      </div>

      {/* Privacy Policy Acceptance */}
      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="privacy"
            checked={formData.privacy_accepted}
            onCheckedChange={(checked) => setFormData({ ...formData, privacy_accepted: checked })}
            className="mt-1"
          />
          <label htmlFor="privacy" className="text-sm text-gray-700 cursor-pointer">
            I agree to the <span className="text-[#21bcfe] font-semibold">Privacy Policy</span> <span className="text-red-500">*</span>
          </label>
        </div>
        {errors.privacy_accepted && (
          <p className="text-red-500 text-xs">{errors.privacy_accepted}</p>
        )}
      </div>

      {/* Compliance Declaration */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="compliance"
            checked={formData.compliance_declaration}
            onCheckedChange={(checked) => setFormData({ ...formData, compliance_declaration: checked })}
            className="mt-1"
          />
          <label htmlFor="compliance" className="text-sm text-gray-700 cursor-pointer">
            I declare that all information provided in this Identity is accurate and complete. I understand that providing false information may result in account suspension.
          </label>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 gap-4">
        <Button
          variant="outline"
          onClick={onBack}
          className="bg-gray-100 hover:bg-gray-200 text-gray-900 h-12 px-6 rounded-xl flex-1"
        >
          Go Back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="bg-[#062136] hover:bg-[#062136]/90 text-white font-semibold h-12 px-8 rounded-xl shadow-lg flex-1 disabled:opacity-50"
        >
          {submitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Submitting...
            </>
          ) : (
            <>
              <Check className="mr-2 w-4 h-4" /> Submit KYC
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
}

// File Upload Component
function FileUpload({ label, value, onChange, accept, error, selfie }) {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = (file) => {
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      const allowedTypes = [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'application/pdf',
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error('File type must be PNG, JPG, JPEG, or PDF');
        return;
      }
      onChange(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  return (
    <div className="space-y-2">
      <Label className="text-gray-900">{label}</Label>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setIsDragging(false)}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          'border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors',
          selfie
            ? isDragging
              ? 'border-[#ff5a5a] bg-red-50' // Selfie drag style
              : 'border-[#ff5a5a] bg-white hover:bg-red-50' // Selfie normal style
            : isDragging
              ? 'border-[#21bcfe] bg-blue-50'
              : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={(e) => handleFileSelect(e.target.files[0])}
          className="hidden"
        />
        <Upload className={cn("w-8 h-8 mb-4 mx-auto", selfie ? "text-[#ff5a5a]" : "text-gray-400")} />
        <p className={cn("text-sm font-medium mb-4", selfie ? "text-[#ff5a5a]" : "text-gray-900")}>Drag & drop or click to upload</p>
        <p className="text-xs text-gray-500">
          {value ? value.name : 'PNG, JPG, JPEG or PDF (Max 10MB)'}
        </p>
        {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
      </div>
    </div>
  );
}

// Identity Status Display
function KYCStatusDisplay({ kycStatus, onResubmit }) {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case 'rejected':
        return <AlertCircle className="w-16 h-16 text-red-500" />;
      case 'in_review':
        return <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />;
      case 'resubmit':
        return <AlertCircle className="w-16 h-16 text-orange-500" />;
      default:
        return <AlertCircle className="w-16 h-16 text-gray-500" />;
    }
  };

  return (
    <Card className="p-8 max-w-2xl mx-auto text-center">
      <div className="mb-6 flex justify-center">
        {getStatusIcon(kycStatus.status)}
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mb-2">Identity Status</h2>
      <span
        className={`inline-block badge ${getStatusBadgeClass(
          kycStatus.status
        )} text-lg px-4 py-2 mb-6`}
      >
        {formatStatusText(kycStatus.status)}
      </span>

      {kycStatus.review_notes && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg text-left">
          <p className="text-gray-600 text-sm mb-1 font-semibold">Review Notes:</p>
          <p className="text-gray-900">{kycStatus.review_notes}</p>
        </div>
      )}

      {kycStatus.submitted_at && (
        <p className="text-gray-500 mt-4 text-sm">
          Submitted on {formatDate(kycStatus.submitted_at)}
        </p>
      )}

      {(kycStatus.status === 'resubmit' || kycStatus.status === 'rejected') && (
        <Button
          onClick={onResubmit}
          className="bg-[#062136] hover:bg-[#062136]/90 text-white mt-6"
        >
          Resubmit Documents
        </Button>
      )}

      {kycStatus.status === 'approved' && (
        <div className="mt-6 bg-green-50 p-4 rounded-lg border border-green-200">
          <p className="text-green-900 text-sm">
            ✅ Your Identity is approved! You can now access all features of the platform.
          </p>
        </div>
      )}
    </Card>
  );
}

// Main KYC Component
const KYC = () => {
  const [step, setStep] = useState(1);
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    // Personal Info
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    nationality: '',
    email: '',
    phone: '',
    // ID Verification
    id_type: '',
    id_number: '',
    id_expiry: '',
    // Address
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    // Financial
    employment_status: '',
    occupation: '',
    annual_income: '',
    source_of_funds: '',
    financial_info: '',
    // Compliance
    politically_exposed_person: '',
    sanctions_check: '',
    terms_accepted: false,
    privacy_accepted: false,
    compliance_declaration: false,
  });

  const [files, setFiles] = useState({
    id_front: null,
    id_back: null,
    selfie: null,
    proof_of_address: null,
  });

  useEffect(() => {
    fetchKYCStatus();
  }, []);

  const fetchKYCStatus = async () => {
    try {
      const response = await kycAPI.getStatus();
      if (
        response.data &&
        response.data.status &&
        response.data.status !== 'not_submitted'
      ) {
        setKycStatus(response?.data);
      }
    } catch (error) {
      console.error('Error fetching Identity Verification status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      const submitData = new FormData();

      // Personal details
      const personalDetails = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        nationality: formData.nationality,
        email: formData.email,
        phone: formData.phone,
      };

      submitData.append('personal_details', JSON.stringify(personalDetails));

      // ✅ Add these as direct fields (NOT nested)
      submitData.append('source_of_funds', formData.source_of_funds);
      submitData.append('compliance_declarations',
        formData.compliance_declaration ? 'accepted' : 'not_accepted'
      );

      // Append files
      if (files.id_front) submitData.append('government_id', files.id_front);
      if (files.id_back) submitData.append('id_back', files.id_back);
      if (files.selfie) submitData.append('selfie', files.selfie);
      if (files.proof_of_address) submitData.append('proof_of_address', files.proof_of_address);

      await kycAPI.submit(submitData);
      toast.success('🎉 Identity submitted successfully! Please wait for verification.');
      await fetchKYCStatus();
      setStep(1);

      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        date_of_birth: '',
        gender: '',
        nationality: '',
        email: '',
        phone: '',
        id_type: '',
        id_number: '',
        id_expiry: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        employment_status: '',
        occupation: '',
        annual_income: '',
        source_of_funds: '', // ← Direct field
        financial_info: '',
        politically_exposed_person: '',
        sanctions_check: '',
        terms_accepted: false,
        privacy_accepted: false,
        compliance_declaration: false,
      });
      setFiles({ id_front: null, id_back: null, selfie: null, proof_of_address: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || '❌ Failed to submit KYC');
      console.error('Identity submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => setStep((s) => Math.min(s + 1, 5));
  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  const handleResubmit = () => {
    setKycStatus(null);
    setStep(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .loading-spinner {
            width: 48px;
            height: 48px;
            border: 4px solid #e5e7eb;
            border-top-color: #062136;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
        `}</style>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Show status if already submitted and not resubmitting
  if (
    kycStatus &&
    kycStatus.status &&
    kycStatus.status !== 'not_submitted' &&
    step === 1
  ) {
    return (
      <div className="space-y-6 animate-fadeIn p-4 sm:p-5">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            Identity Verification
          </h1>
          <p className="text-gray-600">Your Identity verification status</p>
        </div>
        <KYCStatusDisplay kycStatus={kycStatus} onResubmit={handleResubmit} />
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:pl-10 lg:pr-6 lg:py-10 animate-fadeIn bg-[#F9F9F9] min-h-screen w-full">
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
          Identity Verification
        </h1>
        <p className="text-gray-600">Complete your Identity verification to unlock all features</p>
      </div>

      <Card className="rounded-3xl p-4 sm:p-6 lg:p-12 shadow-sm border border-gray-200 mx-auto bg-white">
        <div className="mb-6">
          <h2 className="text-base sm:text-lg lg:text-xl font-medium text-gray-600">
            Identity form <span className="mx-2 text-gray-400">›</span>{' '}
            <span className="text-gray-900 font-semibold">
              {step === 1
                ? 'Personal Information'
                : step === 2
                  ? 'ID Verification'
                  : step === 3
                    ? 'Address Proof'
                    : step === 4
                      ? 'Financial Information'
                      : 'Compliance & Declaration'}
            </span>
          </h2>
        </div>

        <StepIndicator currentStep={step} />

        <AnimatePresence mode="wait">
          {step === 1 && (
            <PersonalInfoStep
              key="step1"
              formData={formData}
              setFormData={setFormData}
              onNext={nextStep}
            />
          )}
          {step === 2 && (
            <IDVerificationStep
              key="step2"
              files={files}
              setFiles={setFiles}
              formData={formData}
              setFormData={setFormData}
              onNext={nextStep}
              onBack={prevStep}
            />
          )}
          {step === 3 && (
            <AddressProofStep
              key="step3"
              files={files}
              setFiles={setFiles}
              formData={formData}
              setFormData={setFormData}
              onNext={nextStep}
              onBack={prevStep}
            />
          )}
          {step === 4 && (
            <FinancialInfoStep
              key="step4"
              formData={formData}
              setFormData={setFormData}
              onNext={nextStep}
              onBack={prevStep}
            />
          )}
          {step === 5 && (
            <ComplianceStep
              key="step5"
              formData={formData}
              setFormData={setFormData}
              onSubmit={handleSubmit}
              submitting={submitting}
              onBack={prevStep}
            />
          )}
        </AnimatePresence>
      </Card>
    </div>
  );
};

export default KYC;
